import { Box, Button, Stack, Textarea, Typography } from "@mui/joy";
import { TaskEditorActionComponent } from "pages/task-editor/utils/taskEditorUtils";
import { PreviewSessionApi } from "./usePreviewSession";
import { previewInputSheetSx } from "./previewStyles";
import { theme } from "styles/theme";
import { useState } from "react";

// v1 의 Selection variant 는 color="primary" (선택) / "secondary" (미선택) 으로 토글.
// theme.ts lines 778–843 참조. width/flex 만 우리가 지정, 시각은 variant 가 처리.
const previewSelectionButtonSx = {
  width: { xs: "100%", sm: "auto" },
  flex: { sm: "1 1 auto" },
};

// 옵션 컨테이너 — v1 ButtonGroupMultipleChoiceCell 의 grid xs / flex sm 패턴.
const previewOptionsContainerSx = {
  width: "100%",
  display: { xs: "grid", sm: "flex" },
  gridTemplateColumns: { xs: "repeat(2, 1fr)" },
  flexWrap: "wrap",
  gap: 1,
  p: "4px",
};

interface PreviewActionProps {
  action: TaskEditorActionComponent;
  blockIndex: number; // 0-based
  blockSequence: number; // 1-based
  mode: "static" | "interactive";
  session: PreviewSessionApi;
  viewerMode: "patient" | "coach";
  totalBlocks: number;
}

function PreviewAction({
  action,
  blockIndex,
  blockSequence,
  mode,
  session,
  viewerMode,
}: PreviewActionProps) {
  const isInteractive = mode === "interactive";

  switch (action.type) {
    case "text_input":
      return (
        <Stack spacing="12px">
          {action.fields.map((field, fieldIdx) => (
            <TextInputField
              key={fieldIdx}
              field={field}
              blockIndex={blockIndex}
              fieldIndex={fieldIdx}
              isInteractive={isInteractive}
              session={session}
            />
          ))}
        </Stack>
      );

    case "button_default":
      return (
        <Box sx={previewOptionsContainerSx}>
          {action.options.map((opt, optIdx) => {
            const selections = session.state.buttonSelections[blockIndex] ?? [];
            const isSelected = selections.includes(optIdx);
            return (
              <Button
                key={optIdx}
                variant={"Selection" as any}
                size="md"
                color={isSelected ? "primary" : "neutral"}
                sx={{
                  ...previewSelectionButtonSx,
                  ...(isInteractive ? {} : { pointerEvents: "none" }),
                }}
                onClick={
                  isInteractive
                    ? (e) => {
                        const el = e.currentTarget as HTMLElement;
                        session.toggleButtonDefault(blockIndex, optIdx);
                        el.blur();
                      }
                    : undefined
                }
              >
                {opt.text || "(옵션 없음)"}
              </Button>
            );
          })}
        </Box>
      );

    case "button_quiz":
      return (
        <QuizButtons
          action={action}
          blockIndex={blockIndex}
          isInteractive={isInteractive}
          viewerMode={viewerMode}
          session={session}
        />
      );

    case "button_branch":
      return (
        <Stack spacing="8px">
          <Box sx={previewOptionsContainerSx}>
            {action.options.map((opt, optIdx) => {
              const selections =
                session.state.buttonSelections[blockIndex] ?? [];
              const isSelected = selections.includes(optIdx);
              return (
                <Button
                  key={optIdx}
                  variant={"Selection" as any}
                  size="md"
                  color={isSelected ? "primary" : "neutral"}
                  sx={{
                    ...previewSelectionButtonSx,
                    ...(isInteractive ? {} : { pointerEvents: "none" }),
                  }}
                  onClick={
                    isInteractive
                      ? (e) => {
                          const el = e.currentTarget as HTMLElement;
                          session.chooseBranch(blockIndex, optIdx);
                          el.blur();
                        }
                      : undefined
                  }
                >
                  {opt.text || "(옵션 없음)"}
                </Button>
              );
            })}
          </Box>
          {viewerMode === "coach" && (
            <Stack spacing="2px" sx={{ pl: "4px" }}>
              {action.options.map((opt, optIdx) => {
                const showFrom = opt.show_from ?? blockSequence + 1;
                return (
                  <Typography
                    key={optIdx}
                    variant="Caption"
                    sx={{ color: theme.vars.palette.Content.Weak }}
                  >
                    {`옵션 ${optIdx + 1} → 블록 ${showFrom}`}
                  </Typography>
                );
              })}
            </Stack>
          )}
        </Stack>
      );

    default: {
      const _never: never = action;
      void _never;
      return null;
    }
  }
}

// 텍스트 입력 필드 개별 컴포넌트
function TextInputField({
  field,
  blockIndex,
  fieldIndex,
  isInteractive,
  session,
}: {
  field: { label?: string; placeholder?: string; help_text?: string };
  blockIndex: number;
  fieldIndex: number;
  isInteractive: boolean;
  session: PreviewSessionApi;
}) {
  const [isFocused, setIsFocused] = useState(false);
  const value = session.state.textInputs[blockIndex]?.[fieldIndex] ?? "";

  return (
    <Stack spacing="6px">
      {field.label && (
        <Typography
          variant="Caption"
          sx={{
            color: theme.vars.palette.Content.Highlight,
            fontWeight: 700,
          }}
        >
          {field.label}
        </Typography>
      )}
      <Box
        sx={{
          ...previewInputSheetSx,
          border: isFocused
            ? `1.4px solid ${theme.vars.palette.Border.Highlight}`
            : `1.4px solid ${theme.vars.palette.Border.Weak}`,
          display: "flex",
          alignItems: "flex-start",
        }}
      >
        {isInteractive ? (
          // v1 PlainTextarea 와 동일한 외형: 보더는 바깥 Box 가 담당하고 Joy
          // Textarea 는 투명·보더 0, 본문 16px/500, minRows=1 로 자동 높이 확장.
          <Textarea
            value={value}
            placeholder={field.placeholder ?? ""}
            onChange={(e) =>
              session.setTextInput(blockIndex, fieldIndex, e.target.value)
            }
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            minRows={1}
            sx={{
              width: "100%",
              minWidth: 0,
              padding: 0,
              backgroundColor: "transparent",
              borderWidth: 0,
              boxShadow: "none",
              "--joy-focus-thickness": "0px",
              "--variant-borderWidth": "0px",
              "--Textarea-gap": "0px",
              "& textarea": {
                fontSize: "16px",
                fontWeight: 500,
                lineHeight: 1.5,
                color: theme.vars.palette.Content.Strong,
              },
              "& textarea::placeholder": {
                color: theme.vars.palette.Content.SuperWeak,
              },
            }}
          />
        ) : (
          <Box
            sx={{
              fontSize: "16px",
              fontWeight: 500,
              lineHeight: 1.5,
              color: theme.vars.palette.Content.Weak,
              minHeight: "24px",
              pointerEvents: "none",
              width: "100%",
            }}
          >
            {field.placeholder && (
              <span style={{ opacity: 0.6 }}>{field.placeholder}</span>
            )}
          </Box>
        )}
      </Box>
      {field.help_text && (
        <Typography
          variant="Caption"
          sx={{ color: theme.vars.palette.Content.Weak }}
        >
          {field.help_text}
        </Typography>
      )}
    </Stack>
  );
}

// 퀴즈 버튼 — v1 Selection variant 기반.
// · 정답: 해당 옵션만 primary(Highlight) 표시 + advance.
// · 오답 시도: 클릭한 옵션을 Negative 보더/색으로 표시해 클릭이 먹었음을 알리고,
//   아래에 incorrect_message 안내를 띄운다. 정답을 맞출 때까지 다시 시도 가능.
function QuizButtons({
  action,
  blockIndex,
  isInteractive,
  viewerMode,
  session,
}: {
  action: Extract<TaskEditorActionComponent, { type: "button_quiz" }>;
  blockIndex: number;
  isInteractive: boolean;
  viewerMode: "patient" | "coach";
  session: PreviewSessionApi;
}) {
  const quizState = session.state.quizState[blockIndex];
  const isCorrect = quizState?.correct === true;
  const tried = quizState?.tried ?? [];
  // 한 번이라도 시도해서 정답을 못 맞춘 상태면 오답 안내 노출
  const showIncorrectAlert =
    isInteractive &&
    !isCorrect &&
    tried.length > 0 &&
    !!action.incorrect_message;

  return (
    <Stack spacing="8px">
      <Box sx={previewOptionsContainerSx}>
        {action.options.map((opt, optIdx) => {
          const isThisCorrect = opt.is_correct === true;
          const isSelectedCorrect = isCorrect && isThisCorrect;
          // 정답 전, 이 옵션을 눌렀지만 오답이었던 경우
          const isTriedWrong =
            !isCorrect && !isThisCorrect && tried.includes(optIdx);
          const clickable = isInteractive && !isCorrect;

          return (
            <Button
              key={optIdx}
              variant={"Selection" as any}
              size="md"
              color={isSelectedCorrect ? "primary" : "neutral"}
              sx={{
                ...previewSelectionButtonSx,
                ...(clickable ? {} : { pointerEvents: "none" }),
                ...(isTriedWrong && {
                  boxShadow: `inset 0 0 0 1.333px ${theme.vars.palette.Content.Negative}`,
                  color: theme.vars.palette.Content.Negative,
                  fontWeight: 600,
                }),
              }}
              onClick={
                clickable
                  ? (e) => {
                      const el = e.currentTarget as HTMLElement;
                      session.attemptQuiz(blockIndex, optIdx);
                      el.blur();
                    }
                  : undefined
              }
            >
              {opt.text || "(옵션 없음)"}
            </Button>
          );
        })}
      </Box>
      {showIncorrectAlert && (
        <Typography
          variant="Caption"
          sx={{
            color: theme.vars.palette.Content.Negative,
            fontWeight: 600,
            pl: "4px",
          }}
        >
          {action.incorrect_message}
        </Typography>
      )}
      {/* coach 정적 모드 진단 정보: 정답 표시 + 오답 메시지 */}
      {viewerMode === "coach" && !isInteractive && (
        <Stack spacing="2px" sx={{ pl: "4px" }}>
          {action.options.map((opt, optIdx) => {
            if (opt.is_correct !== true) return null;
            return (
              <Typography
                key={optIdx}
                variant="Caption"
                sx={{
                  color: theme.vars.palette.Content.Highlight,
                  fontWeight: 700,
                }}
              >
                {`옵션 ${optIdx + 1}: (정답)`}
              </Typography>
            );
          })}
          {action.incorrect_message && (
            <Typography
              variant="Caption"
              sx={{ color: theme.vars.palette.Content.Weak }}
            >
              {`오답 시: ${action.incorrect_message}`}
            </Typography>
          )}
        </Stack>
      )}
    </Stack>
  );
}

export default PreviewAction;
